// tree.ts — the structured output of `parseMarkdown`: a small block/inline tree, framework- and
// DOM-agnostic on purpose. This package has ZERO runtime dependencies and never touches a DOM,
// `innerHTML`, or any renderer API — it only decides WHAT the markup means. Two renderers
// consume this same tree today (`apps/widget/src/markdown.ts`'s DOM renderer, `apps/web`'s React
// renderer for the public playground) and both must treat it identically: every block/inline
// node here is a plain, serializable value, so there is nothing for either renderer to
// reinterpret or drift on. See `parse.ts`'s module comment for the supported Markdown subset and
// the XSS rationale for the link-scheme gate.

// Exported so any renderer that needs the exact same http/https gate applied to a URL from
// elsewhere (e.g. the widget's citation links, which come from the engine's retrieval results,
// not from this package's own parsing) can reuse it rather than duplicating — or worse,
// drifting from — the scheme check.
export const LINK_SCHEME = /^https?:\/\//i;

/** A run of literal text. Never zero-length — `parseMarkdown` only ever produces non-empty text
 * nodes, so renderers don't need to special-case an empty string themselves. */
export interface TextNode {
  type: 'text';
  value: string;
}

/** A single `\n` inside a text run (the "single newline → line break" rule). */
export interface BreakNode {
  type: 'break';
}

/** `**bold**`. Children are always leaves (`text`/`break`) — this subset has no nested
 * bold-inside-bold or bold-inside-link. */
export interface StrongNode {
  type: 'strong';
  children: InlineLeaf[];
}

/** `[text](url)` where `url` passed the `LINK_SCHEME` gate. `href` is the RAW matched url
 * (untrimmed) — matches the widget's historical behavior of setting `href` from the untrimmed
 * capture while only trimming for the scheme test. Children are always leaves, same reasoning
 * as `StrongNode`. */
export interface LinkNode {
  type: 'link';
  href: string;
  children: InlineLeaf[];
}

/** What can appear inside a `strong`/`link`'s children, or directly in a paragraph/list-item's
 * children alongside `strong`/`link` nodes themselves. */
export type InlineLeaf = TextNode | BreakNode;

/** Any inline-level node — a leaf, or one of the two supported inline markers. */
export type InlineNode = InlineLeaf | StrongNode | LinkNode;

/** An ordinary block of text — one or more lines joined by single-`\n` breaks. */
export interface ParagraphBlock {
  type: 'paragraph';
  children: InlineNode[];
}

/** A contiguous run of `- ` lines. Each item's `InlineNode[]` is inline-parsed independently,
 * same as a paragraph's children — bold and links both work inside list items. */
export interface ListBlock {
  type: 'list';
  items: InlineNode[][];
}

/** A single Markdown block, separated from its neighbors by a blank line (`\n{2,}`). */
export type Block = ParagraphBlock | ListBlock;
